import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Rss, AlertTriangle, Pill, Flag } from "lucide-react";
import api from "@/lib/api";

const KIND_META = {
    calculator_critical: { Icon: AlertTriangle, bg: "bg-[#F9ECEC]", ring: "ring-[#9E2A2B]", pill: "bg-[#9E2A2B] text-white", label: "Calculadora crítica" },
    prescription:        { Icon: Pill,         bg: "bg-[#EAF6F2]", ring: "ring-[#2E7D6B]", pill: "bg-[#2E7D6B] text-white", label: "Receita" },
    problem_p0:          { Icon: Flag,         bg: "bg-[#FBEEE6]", ring: "ring-[#C85A47]", pill: "bg-[#C85A47] text-white", label: "Problema P0" },
};

function fmt(iso) {
    if (!iso) return "—";
    return new Date(iso).toLocaleString("pt-BR", {
        day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit",
    });
}

export default function TimelineView() {
    const [events, setEvents] = useState([]);
    const [days, setDays] = useState(30);
    const [loading, setLoading] = useState(true);
    const [kindFilter, setKindFilter] = useState("");

    useEffect(() => {
        (async () => {
            setLoading(true);
            try {
                const { data } = await api.get(`/monitoring/global-timeline?days=${days}`);
                setEvents(data.events || []);
            } finally { setLoading(false); }
        })();
    }, [days]);

    const filtered = kindFilter ? events.filter((e) => e.kind === kindFilter) : events;

    // Group by day for cleaner rail
    const byDay = filtered.reduce((acc, e) => {
        const day = (e.timestamp || "").slice(0, 10) || "—";
        (acc[day] = acc[day] || []).push(e);
        return acc;
    }, {});
    const days_sorted = Object.keys(byDay).sort((a, b) => b.localeCompare(a));

    return (
        <div>
            <div className="card-earthy mb-4 flex flex-wrap items-center gap-3">
                <div className="flex items-center gap-2 text-xs text-muted-clinical">
                    <Rss size={12} /> Janela
                </div>
                {[7, 30, 90].map((d) => (
                    <button
                        key={d}
                        onClick={() => setDays(d)}
                        className={`text-xs px-3 py-1.5 rounded-full transition-colors ${days === d ? "bg-brand text-white" : "bg-alt text-brand hover:bg-[var(--surface-alt-hover,_#EDE5D2)]"}`}
                        data-testid={`timeline-window-${d}`}
                    >
                        {d} dias
                    </button>
                ))}
                <select
                    value={kindFilter}
                    onChange={(e) => setKindFilter(e.target.value)}
                    className="input-strong text-xs ml-2"
                    data-testid="timeline-kind"
                >
                    <option value="">Todos os tipos</option>
                    <option value="calculator_critical">Calculadoras críticas</option>
                    <option value="prescription">Receitas</option>
                    <option value="problem_p0">Problemas P0</option>
                </select>
                <span className="ml-auto text-xs text-muted-clinical">
                    {filtered.length} evento{filtered.length !== 1 ? "s" : ""}
                </span>
            </div>

            {loading ? (
                <p className="text-muted-clinical text-sm">Carregando…</p>
            ) : days_sorted.length === 0 ? (
                <div className="card-earthy text-sm text-muted-clinical">
                    Nenhum evento clínico crítico registrado nesta janela.
                </div>
            ) : (
                <div className="space-y-6" data-testid="global-timeline">
                    {days_sorted.map((day) => (
                        <div key={day}>
                            <div className="text-xs uppercase tracking-widest text-[var(--brand-secondary)] font-semibold mb-2">
                                {day !== "—" ? new Date(day).toLocaleDateString("pt-BR", { weekday: "long", day: "2-digit", month: "long" }) : "—"}
                            </div>
                            <div className="relative pl-6 border-l-2 border-[#EDE0C6] space-y-3">
                                {byDay[day].map((e, i) => {
                                    const meta = KIND_META[e.kind] || {};
                                    const Icon = meta.Icon;
                                    return (
                                        <div key={i} className={`relative ${meta.bg} rounded-lg p-3 ring-1 ${meta.ring}`} data-testid={`timeline-event-${e.patient_id}-${i}`}>
                                            <span className={`absolute -left-[30px] top-3 w-5 h-5 rounded-full ${meta.pill} flex items-center justify-center shadow-md`}>
                                                {Icon && <Icon size={11} />}
                                            </span>
                                            <div className="flex items-start justify-between gap-3">
                                                <div className="min-w-0">
                                                    <Link
                                                        to={`/monitoring/patient/${e.patient_id}`}
                                                        className="text-sm font-medium text-brand hover:underline"
                                                    >
                                                        {e.patient_name}
                                                    </Link>
                                                    <div className="text-xs text-brand mt-0.5">{e.label}</div>
                                                    {e.detail && (
                                                        <div className="text-[11px] text-muted-clinical mt-1 line-clamp-2">{e.detail}</div>
                                                    )}
                                                </div>
                                                <div className="text-[10px] text-muted-clinical shrink-0">{fmt(e.timestamp)}</div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
